import { isImageElement, isVideoElement } from '../../ClipAssets/utils';
import { TRANSITION_MINIMUM_DURATION } from '@/App/remotion/MainPlayer/types/Animations/Transitions/Transitions.constants';
import {
  LifeCycleTransition,
  Transition,
  TransitionLifeCycleType
} from '@/App/remotion/MainPlayer/types/Animations/Transitions/Transitions.types';
import { ImageElement } from '@/App/remotion/MainPlayer/types/Element/ImageElement';
import { VideoElement } from '@/App/remotion/MainPlayer/types/Element/VideoElement';
import { TrackType } from '@/App/remotion/MainPlayer/types/Track/Type';
import { Clip } from '@/domains/asset';
import { getElementDurationInMs, isPlayerConfigValid } from '@/libs/playerConfigUtils';
import { ElementsTrack } from '@/App/remotion/MainPlayer/types/Track/ElementsTrack';

export const updateElementTransition = ({
  element,
  lifeCycleType,
  lifeCycleTransition,
  fps
}: {
  element: ImageElement | VideoElement;
  lifeCycleType: TransitionLifeCycleType;
  lifeCycleTransition: LifeCycleTransition | null;
  fps: number;
}): ImageElement | VideoElement => {
  const updatedElement = {
    ...element,
    transitions: {
      ...(element.transitions ?? {})
    }
  };
  if (lifeCycleType === TransitionLifeCycleType.IN) {
    updatedElement.transitions.in = lifeCycleTransition;
  } else {
    updatedElement.transitions.out = lifeCycleTransition;
  }
  const adjustedTransitions = adjustCounterTransitionDuration({
    element: updatedElement,
    lifeCycleType,
    fps
  });
  updatedElement.transitions = adjustedTransitions;
  return updatedElement;
};

const adjustCounterTransitionDuration = ({
  element,
  lifeCycleType,
  fps
}: {
  element: ImageElement | VideoElement;
  lifeCycleType: TransitionLifeCycleType;
  fps: number;
}): Transition => {
  const transition = structuredClone(element?.transitions ?? {});
  let activeTransition = transition?.in;
  let counterTransition = transition.out;
  if (lifeCycleType === TransitionLifeCycleType.OUT) {
    activeTransition = transition?.out;
    counterTransition = transition?.in;
  }
  if (!activeTransition || !counterTransition) {
    return transition;
  }
  const parentDuration = getElementDurationInMs(element, fps);

  if (parentDuration >= TRANSITION_MINIMUM_DURATION && parentDuration < 2 * TRANSITION_MINIMUM_DURATION) {
    counterTransition.properties.duration = 0;
  }

  if (parentDuration < activeTransition.properties.duration + counterTransition.properties.duration) {
    const remainingDuration = parentDuration - activeTransition.properties.duration;
    if (remainingDuration < TRANSITION_MINIMUM_DURATION) {
      counterTransition.properties.duration = 0;
    } else {
      counterTransition.properties.duration = remainingDuration;
    }
  }

  return transition;
};

const isTransitionApplicable = (
  transition: LifeCycleTransition | null | undefined
): transition is LifeCycleTransition => {
  if (transition && transition.properties.duration > 0) {
    return true;
  }
  return false;
};

export const adjustTransitionDuration = ({
  element,
  fps
}: {
  element: ImageElement | VideoElement;
  fps: number;
}): ImageElement['transitions'] => {
  const { transitions } = element;
  const { in: inTransition, out: outTransition } = structuredClone(transitions ?? {});

  if (!inTransition && !outTransition) {
    return undefined;
  }

  const parentDuration = getElementDurationInMs(element, fps);
  let sumOfTransitionDuration = 0;

  if (isTransitionApplicable(inTransition)) {
    sumOfTransitionDuration += inTransition.properties.duration;
  }
  if (isTransitionApplicable(outTransition)) {
    sumOfTransitionDuration += outTransition.properties.duration;
  }

  if (parentDuration < TRANSITION_MINIMUM_DURATION) {
    if (isTransitionApplicable(inTransition)) {
      inTransition.properties.duration = 0;
    }
    if (isTransitionApplicable(outTransition)) {
      outTransition.properties.duration = 0;
    }
  } else if (parentDuration >= TRANSITION_MINIMUM_DURATION && parentDuration < 2 * TRANSITION_MINIMUM_DURATION) {
    if (isTransitionApplicable(inTransition) && isTransitionApplicable(outTransition)) {
      // Prioritize in transition
      inTransition.properties.duration = Math.min(inTransition.properties.duration, parentDuration);
      outTransition.properties.duration = 0;
    } else if (isTransitionApplicable(inTransition)) {
      inTransition.properties.duration = Math.min(inTransition.properties.duration, parentDuration);
    } else if (isTransitionApplicable(outTransition)) {
      outTransition.properties.duration = Math.min(outTransition.properties.duration, parentDuration);
    }
  } else if (sumOfTransitionDuration > parentDuration) {
    if (isTransitionApplicable(inTransition) && isTransitionApplicable(outTransition)) {
      inTransition.properties.duration = parentDuration / 2;
      outTransition.properties.duration = parentDuration / 2;
    } else if (isTransitionApplicable(inTransition)) {
      inTransition.properties.duration = parentDuration;
    } else if (isTransitionApplicable(outTransition)) {
      outTransition.properties.duration = parentDuration;
    }
  }

  return { in: inTransition ?? null, out: outTransition ?? null };
};

export const updateElementTrackTransitions = (elementTrack: ElementsTrack, fps: number): ElementsTrack => {
  return {
    ...elementTrack,
    elements: elementTrack.elements.map(element => {
      if (isImageElement(element) || isVideoElement(element)) {
        return {
          ...element,
          transitions: adjustTransitionDuration({ element, fps })
        };
      }
      return element;
    })
  };
};

export const findAndReplaceElementTrackItemInPlayerConfig = (
  playerConfig: Clip['asset_metadata']['player_config'],
  elementId: string,
  newElement: ImageElement | VideoElement
): Clip['asset_metadata']['player_config'] => {
  if (!isPlayerConfigValid(playerConfig)) {
    throw new Error('Player config is not valid');
  }
  return {
    ...playerConfig,
    playerConfig: {
      ...playerConfig.playerConfig,
      tracks: playerConfig.playerConfig.tracks.map(track => {
        if (track.type === TrackType.ELEMENTS) {
          return {
            ...track,
            elements: track.elements.map(e => (e.id === elementId ? newElement : e))
          };
        }
        return { ...track };
      })
    }
  };
};
